/* eslint-disable lumapps/no-manual-bems */
import React from 'react';

import { classnames, margin, useClassnames } from '@lumapps/classnames';
import { useCustomizations, MODES } from '@lumapps/customizations';
import { PLACEMENT, Targets, computeCustomizationTargets } from '@lumapps/customizations/api';
import { CustomizationComponent } from '@lumapps/customizations/api/components/CustomizationComponent';
import { notifyCustomizationSubscribers } from '@lumapps/customizations/ducks/thunks';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Navigation } from '@lumapps/lumx-navigation/components/Navigation';
import { NavigationItem, NavigationItemProps } from '@lumapps/lumx-navigation/components/NavigationItem';
import { NavigationSkeletonItem } from '@lumapps/lumx-navigation/components/NavigationSkeleton';
import { ViewMode } from '@lumapps/lumx-navigation/constants';
import { HeadingLevelProvider } from '@lumapps/lumx/react';
import { useDispatch } from '@lumapps/redux/react';
import { useResponsive } from '@lumapps/responsive';
import { Link as RouterLink } from '@lumapps/router/components/Link';
import { useCurrentPageEffect } from '@lumapps/router/hooks/useCurrentPageEffect';
import { usePageContext, PageContext } from '@lumapps/router/hooks/usePageContext';
import { useFaviconBadge } from '@lumapps/shell/hooks/useFaviconBadge';
import { usePageTitle } from '@lumapps/shell/hooks/usePageTitle';

import { MAIN_CONTENT_ID } from '../../constants';
import { PageHeader, PageHeaderProps } from '../PageHeader';

import './index.scss';

export enum PAGE_WIDTH {
    /**
     * until tablet => max-width: calc(100vw - #{$lumx-spacing-unit-big * 2});
     * from tablet to desktop => width: 552px;
     * from desktop => width: 744px;
     */
    NARROW = 'narrow',
    /**
     * until tablet => max-width: calc(100vw - #{$lumx-spacing-unit-big * 2});
     * from tablet to desktop => width: 552px;
     * from desktop => width: 936px; Corresponds to 10/12
     */
    COMPACT = 'compact',
    /**
     * until desktop => max-width: calc(100vw - #{$lumx-spacing-unit-big * 2});
     * from desktop to wide => width: 960px;
     * from wide => width: 1128px; Corresponds to 12/12
     */
    WIDE = 'wide',
    /**
     * no restrictions whatsoever. HEAD'S UP: if you use this, you will need to take care
     * of max sizes and responsive yourself!
     */
    FULL = 'full',
}

export interface PageProps {
    /** Classname to append to component's classnames. */
    className?: string;
    /** The components to show in the body area of the page */
    children?: React.ReactNode;
    /** The id for this page, usually used for tracking purposes */
    id: `${Targets}`;
    /** Whether the page is loading or not */
    isLoading?: boolean;
    /** Page title */
    title: string;
    /** State qualifier appended to the title in the document title only, for example to state that filters are active. */
    titleStateQualifier?: string;
    /**
     * whether the default title for the page should be used or not
     */
    useDefaultTitle?: boolean;
    /**
     * Whether the default title is visually hidden or not. If true, then the title
     * will be visually hidden from the page, but still displayed
     */
    isTitleVisuallyHidden?: boolean;
    /**
     * Whether the page has a white background color or not. Note that this only
     * changes the background color of the page, but not the entire application. If this is true,
     * then the component will be displayed with a white background color, while the
     * background color of the entire app will remain the same. To change the background color
     * of the app, use the configuration.ts file located under apps/front-office/components/Pages/configuration.
     */
    hasBackground?: boolean;
    /** the width of the page displayed */
    width?: PAGE_WIDTH;
    /**
     * When the currently displayed page has different types, passing in this argument will create
     * an additional Customizations Target on the page, specifically for that type. For example, if a Content has
     * the type user_directory, an additional customization target and placement will be added to the page, so if
     * customers want to add a customization to all user directories they can easily do so.
     */
    type?: string;
    /** custom props for the label displayed on the header */
    headerLabelProps?: Partial<PageHeaderProps>;
    /**
     * If you want to code your page header in a separate component, you can do so by coding the PageHeader
     * component in another component, and passing it as a prop here.
     */
    pageHeader?: React.ReactElement<PageHeaderProps>;
    /**
     * if the page is displaying multiple subpages, this prop should be passed in in order to show that
     * determine which is the current subpage displayed. This should go hand in hand with the `subpages` prop.
     * This should be the id of your current subpage
     */
    currentSubpage?: string;
    /**
     * An object that has the information needed in order to display the subpages of your current parent view.
     */
    subpages?: Record<
        string,
        {
            /** label that will be displayed on the UI for the subpage link */
            label: string;
            /** view id. This should correlate with the currentSubpage id */
            view: string;
            /** props needed for rendering the link that the navigation will redirect to */
            linkProps: NavigationItemProps['linkProps'];
            /** subpage icon */
            icon?: NavigationItemProps['icon'];
            /** Will display a `NavigationSkeletonItem` instead */
            isLoading?: boolean;
        }
    >;
    /**
     * DISCLAIMER: You should not hide the navigation in order to use a custom one.
     * Make sure to make use of the integrated navigation in this component.
     * */
    shouldHideNavigation?: boolean;
    /** elements to display before the navigation items */
    beforeNavigationItems?: React.ReactNode;
    /** elements to display after the navigation items */
    afterNavigationItems?: React.ReactNode;
    /**
     * Element displayed before the navigation, outside of its list: above it when it is vertical, and above
     * the pills when it is horizontal. A heading in it needs an explicit level, it is outside of the
     * content's `HeadingLevelProvider`.
     */
    beforeNavigation?: React.ReactNode;
    /**
     * Where the navigation goes. `vertical` puts it, with `beforeNavigation`, in a column on the left of the
     * content, below the header. On mobile, it falls back to horizontal pills, below the header.
     */
    navigationOrientation?: 'horizontal' | 'vertical';
    /** optional entity id that can be provided for enhancing tracking and customizations */
    entityId?: string;
    /** optional page context override for analytics tracking (e.g., playlist page tracking video instead of playlist) */
    pageContextOverride?: PageContext;
}

const CLASSNAME = 'page';

/**
 * The section area of a main front office page. All Front office pages should be wrapped inside
 * this component.
 * @family Front office
 */
export const Page: React.FC<PageProps> = ({
    className,
    children,
    id,
    isLoading = false,
    title,
    titleStateQualifier,
    hasBackground = false,
    width = PAGE_WIDTH.WIDE,
    type,
    headerLabelProps = {},
    isTitleVisuallyHidden = false,
    useDefaultTitle = false,
    pageHeader,
    currentSubpage,
    subpages = {},
    beforeNavigationItems,
    afterNavigationItems,
    shouldHideNavigation = false,
    beforeNavigation,
    navigationOrientation = 'horizontal',
    entityId,
    pageContextOverride,
    ...restOfProps
}) => {
    const { get } = useDataAttributes('front-office');
    const { element } = useClassnames(CLASSNAME);
    const { isMobile } = useResponsive();

    const isVerticalNavigation = navigationOrientation === 'vertical';
    /** On mobile, a vertical navigation falls back to pills, stacked above the content. */
    const navigationViewMode = isVerticalNavigation && !isMobile ? ViewMode.vertical : ViewMode.horizontal;
    const { modes } = useCustomizations();
    const dispatch = useDispatch();
    const onPageChanged = React.useCallback(
        (pageId: string) => {
            dispatch(notifyCustomizationSubscribers(pageId));
        },
        [dispatch],
    );
    const dataAttributes = get({
        element: id,
        action: 'page',
        isMobile,
    });

    const classes = classnames(className, CLASSNAME, `${CLASSNAME}--${width.toLowerCase()}`, {
        [`${CLASSNAME}--is-loading`]: isLoading,
        [`${CLASSNAME}--has-background`]: hasBackground,
        [`${CLASSNAME}--vertical-navigation`]: isVerticalNavigation,
        headless: modes[MODES.HEADLESS],
    });

    usePageTitle(title, currentSubpage && subpages[currentSubpage] ? subpages[currentSubpage].label : undefined, {
        isPageLoading: isLoading,
        titleStateQualifier,
    });
    useFaviconBadge();

    useCurrentPageEffect(id, onPageChanged);

    if (id.includes('-page') && !id.includes('error-page') && !id.startsWith('loading-')) {
        throw new Error(
            `The id "${id}" is not allowed for the Page component. Please use a different id that does not include the word "page".`,
        );
    }

    // Set page context for both analytics and agent chat (single dispatch)
    // Store base page data - analytics package will map to analytics props as needed
    // If override is provided (e.g., playlist tracking video), use that instead
    const pageContextData = React.useMemo(
        () =>
            pageContextOverride || {
                pageType: id,
                pageEntityId: entityId || '',
                pageTitle: title,
            },
        [id, entityId, title, pageContextOverride],
    );

    usePageContext(pageContextData);

    const context = React.useMemo(
        () => ({
            id: entityId,
            type: id,
            title,
            isMobile,
            isLoading,
        }),
        [entityId, id, isLoading, isMobile, title],
    );

    const {
        id: computedPageId,
        type: computedPageType,
        shouldAddPageTypeCustomization,
    } = computeCustomizationTargets(id, type);

    const header = (
        <>
            {useDefaultTitle && !pageHeader ? (
                <PageHeader
                    title={title}
                    id={id}
                    keepDefaultTypography
                    {...headerLabelProps}
                    isTitleVisuallyHidden={isTitleVisuallyHidden}
                />
            ) : null}

            {pageHeader || null}
        </>
    );

    const navigation =
        subpages && currentSubpage && !shouldHideNavigation ? (
            <Navigation
                viewMode={navigationViewMode}
                className={navigationViewMode === ViewMode.horizontal ? margin('vertical', 'huge') : undefined}
            >
                {beforeNavigationItems}

                {Object.keys(subpages).map((key) => {
                    const { label, linkProps, view, icon, isLoading } = subpages[key];

                    return !isLoading ? (
                        <NavigationItem
                            key={view}
                            element="a"
                            linkAs={RouterLink}
                            label={label}
                            linkProps={linkProps}
                            isCurrentPage={view === currentSubpage}
                            icon={icon}
                        />
                    ) : (
                        <NavigationSkeletonItem key={view} width={100} />
                    );
                })}

                {afterNavigationItems}
            </Navigation>
        ) : null;

    return (
        <>
            {/**
             * Customizations on this page, for the ABOVE placement follow a specific hierarchy, depending on specificity
             * - First, we render the most generic customization, which is for PAGE, which applies to all pages
             * - Second, we render the customizations associated to a specific PAGE id, like CONTENT or SEARCH
             * - Third, we render the most specific customizations, which target either a specific content type, like NEWS, or a
             *   specific content, like CONTENT-<id>
             */}
            <CustomizationComponent target={Targets.PAGE} placement={PLACEMENT.ABOVE} context={context} />

            {computedPageId !== Targets.APP ? (
                <CustomizationComponent target={computedPageId} placement={PLACEMENT.ABOVE} context={context} />
            ) : null}

            {shouldAddPageTypeCustomization ? (
                <CustomizationComponent target={computedPageType} placement={PLACEMENT.ABOVE} context={context} />
            ) : null}

            <main
                /**
                 * Even though it might seem redundant to add a "main" role to the "main" tag,
                 * it's actually a good practice as it has a larger assistive technology compatibility.
                 */
                role="main"
                id={MAIN_CONTENT_ID}
                className={classes}
                {...dataAttributes}
                {...restOfProps}
            >
                {header}

                {isVerticalNavigation ? (
                    <div className={element('columns')}>
                        {beforeNavigation || navigation ? (
                            <div className={element('navigation')}>
                                {beforeNavigation}
                                {navigation}
                            </div>
                        ) : null}
                        <div className={element('content')}>
                            <HeadingLevelProvider>{children}</HeadingLevelProvider>
                        </div>
                    </div>
                ) : (
                    <>
                        {beforeNavigation}
                        {navigation}
                        <HeadingLevelProvider>{children}</HeadingLevelProvider>
                    </>
                )}
            </main>

            {/**
             * Customizations on this page, for the UNDER placement follow a specific hierarchy, depending on specificity
             * - First, we render the most specific customizations, which target either a specific content type, like NEWS, or a
             *   specific content, like CONTENT-<id>
             * - Second, we render the customizations associated to a specific PAGE id, like CONTENT or SEARCH
             * - Third, we render the most generic customization, which is for PAGE, which applies to all pages
             */}
            {shouldAddPageTypeCustomization ? (
                <CustomizationComponent target={computedPageType} placement={PLACEMENT.UNDER} context={context} />
            ) : null}

            {computedPageId !== Targets.APP ? (
                <CustomizationComponent target={computedPageId} placement={PLACEMENT.UNDER} context={context} />
            ) : null}

            <CustomizationComponent target={Targets.PAGE} placement={PLACEMENT.UNDER} context={context} />
        </>
    );
};
